import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Wordmark } from "@/components/brand/Wordmark";
import { AuthCloseButton } from "@/components/AuthCloseButton";
import { TurnstileWidget } from "@/components/TurnstileWidget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BRAND } from "@/config/brand";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { usePageMeta } from "@/lib/seo";

export default function SignIn() {
  const { t } = useTranslation();
  usePageMeta({ title: `${t("auth.signInTitle")} — AOS Equestrian`, robots: "noindex, nofollow" });
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tsToken, setTsToken] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const user = await login(email, password, { turnstile_token: tsToken });
      toast(t("auth.welcomeToast", { name: user.name }));
      // B1: one account, mode-aware landing — last active mode wins for organizers.
      const lastMode = localStorage.getItem("aos_mode");
      const fallback = user.role === "buyer" || lastMode === "buyer" ? "/account" : "/dashboard";
      // ?next= (sesión caducada): retorno seguro solo a rutas internas
      const next = new URLSearchParams(location.search).get("next");
      const safeNext = next && next.startsWith("/") && !next.startsWith("//") ? next : null;
      navigate(safeNext || location.state?.from || fallback, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || t("auth.genericError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-20" data-testid="signin-page">
      <AuthCloseButton />
      <div className="w-full max-w-md flex flex-col gap-10">
        <div className="flex flex-col items-start gap-6">
          <Wordmark />
          <div>
            <h1 className="text-3xl sm:text-4xl font-medium tracking-tight text-[#0A0A0A]">
              {t("auth.signInTitle")}
            </h1>
            <p className="mt-2 text-sm text-[#737373]">{t("auth.signInSubtitle")}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-6" noValidate>
          <div className="flex flex-col gap-2">
            <Label htmlFor="signin-email" className="text-sm font-medium">{t("auth.email")}</Label>
            <Input
              id="signin-email"
              data-testid="signin-email-input"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("auth.emailPlaceholder")}
              className="h-12 rounded-xl border-black/10 focus-visible:ring-1 focus-visible:ring-[#0A0A0A] bg-transparent"
            />
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="signin-password" className="text-sm font-medium">{t("auth.password")}</Label>
              <Link
                to="/forgot-password"
                data-testid="signin-forgot-link"
                className="text-xs font-medium text-[#0A0A0A]/60 hover:text-[#1D4D3B] transition-colors duration-200"
              >
                {t("auth.forgotLink")}
              </Link>
            </div>
            <Input
              id="signin-password"
              data-testid="signin-password-input"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 rounded-xl border-black/10 focus-visible:ring-1 focus-visible:ring-[#0A0A0A] bg-transparent"
            />
          </div>

          {error && (
            <p data-testid="signin-error" role="alert" className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
              {error}
            </p>
          )}

          <TurnstileWidget onToken={setTsToken} />

          <Button
            type="submit"
            data-testid="signin-submit-button"
            disabled={submitting}
            className="h-12 rounded-full bg-[#0A0A0A] text-white hover:bg-black/80 text-base transition-colors duration-300 disabled:opacity-60"
          >
            {submitting ? `${t("common.loading")}…` : t("auth.signInBtn")}
          </Button>
        </form>

        <p className="text-sm text-[#737373]">
          {t("auth.noAccount", { brand: BRAND.name })}{" "}
          <Link to="/signup" data-testid="signin-to-signup-link" className="font-medium text-[#0A0A0A] underline underline-offset-4 hover:text-[#1D4D3B] transition-colors duration-200">
            {t("auth.signUpLink")}
          </Link>
        </p>
      </div>
    </div>
  );
}
